import { useMemo } from 'react';
import { core_v1_DataSource } from '@antimatterhq/asl-api-ts-v1';
import { Fieldset } from '@antimatterhq/ui/components/fieldset';
import Spacer from '@antimatterhq/ui/components/spacer';
import { JsonExtractReview } from '../jsonExtract/JsonExtractReview';
import { UnreferencedColumnsReview } from '../preserveUnreferencedColumns/UnreferencedColumnsReview';
import { FiltersReview } from '../filters/FiltersReview';
import { FieldSpecReview } from '../fieldSpec/FieldSpecReview';
import { CustomNotebookFunctionReview } from '../customNotebookFunction/CustomNotebookFunctionReview';
import { BronzeTablesReview } from '../additionalBronze/AdditionalBronzeTablesReview';
import { getFieldSchemaValues, getFieldTableRows } from '@/lib/datasource';
import { useGetDatasourcePreset } from '@/hooks/content/useGetDatasourcePreset';
import { filterNulls } from '@/utils/filterNulls';

export type PreTransformReviewProps = {
  dataSource: core_v1_DataSource;
  presetVersion?: number;
};

export const PreTransformReview = ({
  dataSource,
  presetVersion,
}: PreTransformReviewProps) => {
  const { data: presets } = useGetDatasourcePreset({
    name: dataSource.spec.usePreset || undefined,
    version: presetVersion || dataSource.spec.usePresetVersion,
  });

  const preset = useMemo(
    () =>
      presets?.silver?.preTransform?.find(
        ({ name }) => name === dataSource.spec.usePreset,
      ),
    [presets, dataSource.spec.usePreset],
  );

  const fieldSpecData = useMemo(() => {
    const fields = getFieldTableRows(
      filterNulls(preset?.fields),
      getFieldSchemaValues(
        filterNulls(dataSource.spec.silver?.preTransform?.addFields),
      ),
      filterNulls(
        dataSource.spec.silver?.preTransform?.presetOverrides?.omitFields,
      ),
    );
    return fields;
  }, [preset, dataSource]);

  const preTransform = dataSource.spec.silver?.preTransform;

  const temporaryFields = getFieldTableRows(
    undefined,
    getFieldSchemaValues(filterNulls(preTransform?.utils?.temporaryFields)),
    undefined,
  );

  const { unreferencedColumns = {}, jsonExtract = [] } =
    dataSource.spec.silver?.preTransform?.utils ?? {};
  const { filter = '', postFilter = '' } =
    dataSource.spec.silver?.preTransform ?? {};
  const bronzeTables = dataSource.spec.silver?.bronzeTables || [];

  if (dataSource?.spec.silver?.preTransform?.skipPreTransform) {
    return (
      <div>
        <div>
          <Fieldset.Field>
            <Fieldset.Label>No pre-transform</Fieldset.Label>
          </Fieldset.Field>
        </div>
      </div>
    );
  }

  if (dataSource?.spec.silver?.preTransform?.custom) {
    return (
      <CustomNotebookFunctionReview
        custom={dataSource.spec.silver?.preTransform?.custom}
      />
    );
  }

  return (
    <div>
      <div>
        <Fieldset.Field>
          <Fieldset.Label>Preset:</Fieldset.Label>
          <Fieldset.Value data-testid="review-pretransform-preset">
            {dataSource.spec?.silver?.preTransform?.usePreset}
          </Fieldset.Value>
        </Fieldset.Field>
      </div>

      <Spacer size="lg" />

      <BronzeTablesReview bronzeTables={bronzeTables} />

      <Spacer size="lg" />

      <div>
        <Fieldset.Field>
          <Fieldset.Label>Field Summary:</Fieldset.Label>
        </Fieldset.Field>
        <Fieldset.List>
          <FieldSpecReview fields={fieldSpecData} />
        </Fieldset.List>
      </div>

      <Spacer size="lg" />

      <FiltersReview filter={filter || ''} postFilter={postFilter || ''} />

      <Spacer size="lg" />

      <UnreferencedColumnsReview unreferencedColumns={unreferencedColumns} />

      <Spacer size="lg" />

      <JsonExtractReview jsonExtract={jsonExtract} />

      <Spacer size="lg" />

      <div>
        <Fieldset.Field>
          <Fieldset.Label>Temporary Fields:</Fieldset.Label>
        </Fieldset.Field>
        <Fieldset.List>
          <FieldSpecReview fields={temporaryFields} />
        </Fieldset.List>
      </div>
    </div>
  );
};
